import { showToast } from './form-helpers.js';
import { isGuestUser, fetchJson } from '../../http/utils.js';
import { logger } from './logger.js';
import { escapeHtml } from './sanitize.js';
import { icon } from '../../shared/icons/render.js';

const attachedTargets = new WeakSet();
const AUTH_DISABLED_ATTR = 'data-auth-disabled';

// #1076 R2 (2026-06-20): the in-page "Zahlung fortsetzen" button is a SECOND
// resume surface. The old in-page API resume (resume_checkout_for_registration
// -> Stripe redirect) could NOT collect the organizer-terms consent, so for a
// terms-active reg with consent_organizer_terms=0 the backend charge-guard
// returns organizer_terms_required -> dead-end (no way to tick the box here).
// Fix: NAVIGATE to the standalone /checkout/resume/<reg> wrapper page, which
// renders the terms checkbox (resume.html) and then submits with the boolean
// consent — version/snapshot/content_hash stay 100% server-derived under the
// lock. Mirrors pending-recovery.js::onResumeClick (538-546). The target is a
// HARDCODED relative path + encodeURIComponent(regId) — no client-derived URL,
// no Open-Redirect surface. Guest gating stays via disableGuestResumeButtons.
function resumePayment(regId) {
  if (!regId) {
    showToast(__('Konnte Zahlung nicht fortsetzen.'), 'error');
    return;
  }
  const target = `/checkout/resume/${encodeURIComponent(regId)}`;
  try {
    window.location.assign(target);
  } catch (error) {
    logger.warn(error);
    window.location.href = target;
  }
}

function findResumeListenerTarget(host) {
  const feedbackHost = document.getElementById('payment-feedback');

  if (!host) {
    return document;
  }

  if (!feedbackHost) {
    return host;
  }

  if (typeof host.contains === 'function' && host.contains(feedbackHost)) {
    return host;
  }

  let current = host.parentElement;
  while (current) {
    if (current.contains(feedbackHost)) {
      return current;
    }
    current = current.parentElement;
  }

  return document;
}

function disableGuestResumeButtons(scope) {
  const root = scope || document;
  if (!root || typeof root.querySelectorAll !== 'function') {
    return;
  }
  root.querySelectorAll('.resume-payment-btn').forEach((button) => {
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute(AUTH_DISABLED_ATTR, '1');
    button.classList.add('opacity-60', 'cursor-not-allowed');
    if (!button.title) {
      button.title = __('Bitte einloggen, um die Zahlung fortzusetzen.');
    }
  });
}

function initResumePaymentListener(host) {
  const target = findResumeListenerTarget(host);
  if (attachedTargets.has(target)) {
    return;
  }
  attachedTargets.add(target);
  if (isGuestUser()) {
    disableGuestResumeButtons(target);
    return;
  }
  target.addEventListener('click', (event) => {
    const button = event.target.closest('.resume-payment-btn');
    if (!button) {
      return;
    }
    const regId = button.dataset.reg;
    if (regId) {
      event.preventDefault();
      resumePayment(regId);
    }
  });
}

function cleanUrl() {
  if (!history.replaceState) {
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.delete('payment');
  url.searchParams.delete('reg');
  url.searchParams.delete('token');
  if (url.hash) {
    const rawHash = url.hash.replace(/^#/, '');
    if (rawHash.includes('=')) {
      const hashParams = new URLSearchParams(rawHash);
      hashParams.delete('token');
      const nextHash = hashParams.toString();
      url.hash = nextHash ? `#${nextHash}` : '';
    } else if (rawHash) {
      url.hash = '';
    }
  }
  history.replaceState(null, '', url.toString());
}

function row(label, valueHtml) {
  return `
    <div class="flex items-start gap-3">
      <div class="text-secondary min-w-[120px]">${escapeHtml(label)}</div>
      <div class="font-medium text-heading">${valueHtml}</div>
    </div>`;
}

function safeImageSrc(base64) {
  if (!base64 || typeof base64 !== 'string') return '';
  if (!base64.startsWith('data:image/')) return '';
  return base64;
}

function extractOutcomeToken() {
  const qs = new URLSearchParams(window.location.search);
  let token = qs.get('token') || '';

  if (!token && window.location.hash) {
    const rawHash = window.location.hash.replace(/^#/, '');
    if (rawHash.includes('=')) {
      const hashParams = new URLSearchParams(rawHash);
      token = hashParams.get('token') || '';
    } else {
      token = rawHash || '';
    }
  }

  return (token || '').trim();
}

async function fetchOutcome(token, maxRetries = 3) {
  if (!token) {
    return { success: false, toast: __('Token fehlt.'), code: 'missing_token', data: {} };
  }
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const { res, payload } = await fetchJson(
        '/api/method/cita_events.api.payments.checkout_result.get_checkout_outcome',
        { method: 'POST', body: { token }, cache: 'no-store' }
      );
      // P1-7 FIX: Only retry on server errors (>= 500) or network failures.
      // Client errors (4xx) are not retryable.
      if (res && res.status >= 500) {
        throw new Error(`server_error_${res.status}`);
      }
      if (!payload?.success) {
        // 4xx or payload error — not retryable
        return { success: false, toast: payload?.toast || __('Konnte Ergebnis nicht laden.'), code: 'fetch_error', data: {} };
      }
      return payload || {};
    } catch (error) {
      // Show loading indicator after first failed attempt
      if (attempt === 0) {
        const host = document.getElementById('payment-feedback');
        if (host && !host.querySelector('.outcome-retry-indicator')) {
          const indicator = document.createElement('div');
          indicator.className = 'outcome-retry-indicator text-center py-4 text-muted';
          indicator.textContent = __('Ergebnis wird geladen...');
          host.prepend(indicator);
        }
      }
      if (attempt < maxRetries - 1) {
        const jitter = Math.random() * 500;
        await new Promise(r => setTimeout(r, 1000 * Math.pow(2, attempt) + jitter));
      }
    }
  }
  return { success: false, toast: __('Konnte Ergebnis nicht laden.'), code: 'fetch_error', data: {} };
}

function renderBox(innerHtml) {
  const host = document.getElementById('payment-feedback');
  if (!host) {
    return;
  }
  host.innerHTML = `
    <div class="rounded-2xl border border-default-theme surface-page shadow-xl p-4 sm:p-5 mb-6">
      ${innerHtml}
    </div>`;
  try {
    host.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } catch (error) {
    logger.warn(error);
  }
}

function renderSuccess(reg, eventSlug) {
  showToast(__('Zahlung erfolgreich. Dein Ticket ist bereit'), 'success');
  const amount = reg.amount != null ? `${Number(reg.amount).toFixed(2)} ${escapeHtml(reg.currency || 'EUR')}` : '—';
  const email = escapeHtml(reg.email || reg.user || '—');
  const safeQrSrc = safeImageSrc(reg.qr_image_base64);
  const qr = safeQrSrc
    ? `<img src="${safeQrSrc}" alt="QR" class="w-28 h-28 rounded bg-white shadow-inner border border-slate-300/40"/>`
    : '';
  const safeSlug = encodeURIComponent(eventSlug || '');

  renderBox(`
    <div class="flex items-start gap-4">
      <div class="shrink-0">
        <div class="w-11 h-11 grid place-content-center rounded-lg bg-emerald-700 text-white">
          ${icon('check', { size: 24 })}
        </div>
      </div>
      <div class="grow">
        <div class="text-lg font-bold text-emerald-300">${__('Zahlung bestätigt')}</div>
        <div class="text-secondary">${__('Deine Registrierung ist abgeschlossen. Du erhältst gleich eine Bestätigung per E-Mail.')}</div>
      </div>
    </div>

    <div class="mt-4 space-y-2">
      ${row(__('Registrierung'), `<code class="text-xs">${escapeHtml(reg.id || '—')}</code>`)}
      ${row(__('Ticket'), escapeHtml(reg.ticket_label || reg.ticket_type || '—'))}
      ${row(__('Betrag'), amount)}
      ${row(__('E-Mail'), email)}
      ${qr ? row('QR-Code', qr) : ''}
    </div>

    <div class="mt-4 flex flex-wrap gap-2">
      ${reg.pdf_url ? `
        <a href="${encodeURI(reg.pdf_url)}" target="_blank"
           class="px-4 py-2 rounded-lg surface-elevated border border-strong-theme text-white hover:surface-elevated inline-flex items-center gap-2">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2h9l5 5v15H6z"/><path d="M14 2v6h6"/></svg>
          ${__('PDF Ticket')}
        </a>` : ''}
      <a href="/event/${safeSlug}/zugang"
         class="px-4 py-2 rounded-lg bg-emerald-700 border border-emerald-600 text-white hover:bg-emerald-600 inline-flex items-center gap-2">
        <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12h18M12 3l9 9-9 9"/></svg>
        ${__('Ticket-Übersicht')}
      </a>
    </div>
  `);

  try {
    window.dispatchEvent(new CustomEvent('payment:success', { detail: reg }));
  } catch (error) {
    logger.warn(error);
  }
}

function renderCancelOrPending(regId, outcome, msg) {
  const isPending = outcome === 'pending';
  const title = isPending ? __('Zahlung noch offen') : __('Zahlung abgebrochen');
  const tone = isPending ? 'text-amber-300 bg-amber-700' : 'text-rose-300 bg-rose-800';
  const safeRegId = escapeHtml(regId || '');
  const safeMsg = msg ? escapeHtml(msg) : '';
  showToast(isPending ? __('Zahlung noch nicht abgeschlossen.') : __('Zahlung abgebrochen.'), isPending ? 'warning' : 'error');

  renderBox(`
    <div class="flex items-start gap-4">
      <div class="shrink-0">
        <div class="w-11 h-11 grid place-content-center rounded-lg ${tone}">
          <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
            ${isPending ? '<path d="M12 6v6l4 2"/><circle cx="12" cy="12" r="10"/>' : '<path d="M18 6L6 18M6 6l12 12"/>'}
          </svg>
        </div>
      </div>
      <div class="grow">
        <div class="text-lg font-bold">${escapeHtml(title)}</div>
        <div class="text-secondary">${safeMsg || (isPending ? __('Du kannst die Zahlung jetzt fortsetzen.') : __('Möchtest du es noch einmal versuchen?'))}</div>
      </div>
    </div>

    <div class="mt-4 flex flex-wrap gap-2">
      <button type="button" class="resume-payment-btn px-4 py-2 rounded-lg surface-elevated border border-strong-theme text-white hover:surface-elevated" data-reg="${safeRegId}">
        ${__('Zahlung fortsetzen')}
      </button>
      <a href="#tickets" class="px-4 py-2 rounded-lg surface-elevated border border-strong-theme text-heading hover:bg-[var(--cita-bg-hover)]">
        ${__('Später erledigen')}
      </a>
    </div>
  `);
  if (isGuestUser()) {
    disableGuestResumeButtons(document);
  }
}

function renderHardError(message) {
  const safeMessage = escapeHtml(message || __('Es ist ein Fehler aufgetreten.'));
  showToast(message || __('Unbekanntes Ergebnis.'), 'error');
  renderBox(`
    <div class="flex items-start gap-4">
      <div class="shrink-0">
        <div class="w-11 h-11 grid place-content-center rounded-lg bg-rose-800 text-white">
          ${icon('shieldAlert', { size: 24 })}
        </div>
      </div>
      <div class="grow">
        <div class="text-lg font-bold text-rose-300">${__('Fehler')}</div>
        <div class="text-secondary">${safeMessage}</div>
      </div>
    </div>

    <div class="mt-4 flex flex-wrap items-center gap-3">
      <a href="#tickets" class="px-4 py-2 rounded-lg surface-elevated border border-strong-theme text-white hover:surface-elevated">
        ${__('Zurück zu den Tickets')}
      </a>
      <a href="/support" class="text-sm text-cyan-400 hover:text-cyan-300 transition">
        ${__('Hilfe vom Support')}
      </a>
    </div>
  `);
}

function handlePaymentFlags(config) {
  const qs = new URLSearchParams(window.location.search);
  const paymentFlag = (qs.get('payment') || '').toLowerCase();
  const regId = qs.get('reg') || '';
  const token = extractOutcomeToken();

  if (!paymentFlag) {
    return;
  }

  cleanUrl();

  (async () => {
    try {
      if ((paymentFlag === 'cancel' || paymentFlag === 'error') && !token) {
        renderHardError(paymentFlag === 'cancel' ? __('Du hast die Zahlung abgebrochen.') : __('Die Zahlung konnte nicht abgeschlossen werden.'));
        return;
      }

      const info = token ? await fetchOutcome(token) : null;
      if (!info || info.success === false) {
        if (paymentFlag === 'success') {
          // FIX-D: Reassure user when payment succeeded but outcome fetch failed
          showToast(__('Zahlung erfolgreich.'), 'success');
          renderBox(`
            <div class="flex items-start gap-4">
              <div class="shrink-0">
                <div class="w-11 h-11 grid place-content-center rounded-lg bg-emerald-800 text-white">
                  ${icon('check', { size: 24 })}
                </div>
              </div>
              <div class="grow">
                <div class="text-lg font-bold text-emerald-300">${__('Zahlung wird verarbeitet')}</div>
                <div class="text-secondary">${__('Deine Zahlung wird verarbeitet. Du erhältst in Kürze eine Bestätigungsmail.')}</div>
              </div>
            </div>
          `);
        } else if (paymentFlag === 'cancel') {
          showToast(__('Zahlung abgebrochen.'), 'warning');
          if (token) {
            renderHardError(info?.toast || __('Keine Details gefunden.'));
          }
        } else {
          showToast(__('Fehler bei der Zahlung.'), 'error');
          if (token) {
            renderHardError(info?.toast || __('Keine Details gefunden.'));
          }
        }
        return;
      }

      const outcome = info.data?.outcome;
      const reg = info.data?.registration || {};

      if (paymentFlag === 'success' || outcome === 'success') {
        renderSuccess(reg, config?.eventSlug || '');
        return;
      }

      if (paymentFlag === 'cancel') {
        renderCancelOrPending(reg.id || regId, 'failed', __('Du kannst die Zahlung mit einem Klick fortsetzen.'));
        return;
      }

      if (outcome === 'pending') {
        renderCancelOrPending(reg.id || regId, 'pending');
      } else {
        renderHardError(__('Die Zahlung wurde nicht bestätigt.'));
      }
    } catch (error) {
      logger.error('Payment outcome handling failed:', error);
      renderHardError(__('Zahlung konnte nicht verifiziert werden.'));
    }
  })();
}

export function initPaymentHandling({ host, config } = {}) {
  handlePaymentFlags(config);
  if (isGuestUser()) {
    disableGuestResumeButtons(host || document);
    return;
  }
  initResumePaymentListener(host || document);
}
